import React, { useMemo, useCallback } from "react"
import { Link, useParams } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"

import { getDurationDate } from "utils/date"

import {
  Box,
  CardContent,
  CardMedia,
  Fab,
  List,
  Typography,
  useTheme,
  Divider,
  Stack,
  Button,
} from "@mui/material"

import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"
import { useMobileUrl } from "hooks/useMobileUrl"

import { CalendarIcon, LocationIcon } from "../../components/icons"
import { MotionDiv, showContent } from "components/motions"
import { BackIcon } from "components/icons"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { Head } from "components/Head"
import { InfoListItem } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { getIcon } from "utils/icon"
import { FooterAction } from "extensions/one-siam/components/domains/navigation/FooterAction"
import { SearchIcon } from "extensions/one-siam/components/icons"
import { AppNavigation } from "extensions/one-siam/container/kiosk-v"
import { ImageCarouselModal } from "components/Image"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { Carousel, CarouselItem } from "components/Carousel"
import { size } from "lodash"

export const PromotionDetailKioskVPage = ({ Layout = Box }) => {
  const params = useParams()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const { promotionId } = params
  const { promotion, navigateToLocation } = usePromotionDetail(promotionId)
  const { goBack } = useAppUI()

  const theme = useTheme()

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarIcon />,
    [theme]
  )

  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    root,
    actionButton,
    actionContainer,
    backButton,
    backButtonWrapper,
    buttonContainer,
    button,
    cardContentTextWrapper,
    cardMediaWrapper,
    card,
    cardContent,
    cardHeader,
    cardContentSecondaryText,
    contentWrapper,
    coverBanner,
    detailWrapper,
    divider,
    infoContainer,
    infoListItem,
    headerWrapper,
    navigationWrapper,
    pageWrapper,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrCodeSecondaryText,
    searchBarContainer,
    toolbarContainer,
    mainMenuContainer,
    footerActionContainer,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
  } = styleOverrides
  const { galleryCarouselOption } =
    theme.components[`VuiDetailCard`]?.defaultProps || {}

  const { t } = useTranslation()

  const { mobileUrl } = useMobileUrl()

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const allImagesUrls = useMemo(
    () =>
      size(promotion?.allImages) > 0
        ? promotion?.allImages?.map((v) => v?.url)
        : [],
    [promotion?.allImages]
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  return (
    <>
      <Head title={`${t(promotion?.name || "")} (${t("Promotion Detail")})`} />
      <Layout>
        <Box sx={root}>
          <MotionDiv
            variants={showContent}
            initial="initial"
            animate="animate"
            sx={pageWrapper}
          >
            {!promotion ? null : (
              <>
                <Box
                  sx={{
                    ...coverBanner,
                    backgroundImage: `url(${promotion.coverImage})`,
                  }}
                />
                <MotionDiv sx={contentWrapper}>
                  <Box sx={card}>
                    <Box sx={cardMediaWrapper}>
                      {promotion.allImages && (
                        <Box sx={galleryCarouselWrapper}>
                          <Carousel
                            options={{
                              type: "fade",
                              padding: 0,
                              gap: 8,
                              perPage: 1,
                              pagination: allImagesUrls.length > 1,
                              autoplay: true,
                              ...galleryCarouselOption,
                            }}
                            sx={galleryCarousel}
                          >
                            {promotion.allImages.map(({ id, url }) => {
                              return (
                                <CarouselItem
                                  key={`slide-${id}`}
                                  sx={galleryCarouselItem}
                                >
                                  <CardMedia
                                    component="img"
                                    sx={galleryCarouselItem}
                                    src={url}
                                    onClick={() => handleImage(url)}
                                  />
                                </CarouselItem>
                              )
                            })}
                          </Carousel>
                        </Box>
                      )}
                    </Box>
                    <Box sx={detailWrapper}>
                      <Box sx={headerWrapper}>
                        <Typography
                          sx={cardHeader}
                          variant="title"
                          component={"h2"}
                        >
                          {t(promotion.name)}
                        </Typography>
                      </Box>
                      <CardContent sx={cardContent}>
                        <Stack direction="row">
                          {promotion.showInfo && (
                            <List sx={infoContainer}>
                              {promotion.hasLocation && (
                                <InfoListItem
                                  sx={infoListItem}
                                  icon={<LocationIcon />}
                                  primary={
                                    <LocationInfo
                                      feature={promotion.feature}
                                      component="span"
                                    />
                                  }
                                />
                              )}
                              {!promotion.showAlways && (
                                <InfoListItem
                                  sx={infoListItem}
                                  icon={calendarIcon}
                                  primary={getDurationDate(
                                    promotion.startDate,
                                    promotion.endDate,
                                    { monthFormat: "MMMM" }
                                  )}
                                />
                              )}
                            </List>
                          )}
                        </Stack>
                        <Divider sx={divider} />
                        <Box sx={cardContentTextWrapper}>
                          <Typography
                            variant="body2"
                            sx={cardContentSecondaryText}
                            dangerouslySetInnerHTML={{
                              __html: promotion.descriptionText,
                            }}
                          ></Typography>
                        </Box>
                        <Box sx={qrCodeContainer} className="target">
                          <QRCode sx={qrCode} url={mobileUrl} />
                          <Stack>
                            <Typography
                              variant="subtitle2"
                              component="p"
                              sx={qrcodePrimaryText}
                            >
                              {t("Scan to view")}
                            </Typography>
                            <Typography
                              variant="caption"
                              component="p"
                              sx={qrCodeSecondaryText}
                            >
                              {t("On mobile")}
                            </Typography>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Box>
                  </Box>
                </MotionDiv>
              </>
            )}
          </MotionDiv>
        </Box>
        <Box key="app-navigation" sx={navigationWrapper}>
          <Box sx={backButtonWrapper}>
            <Fab variant="rounded" sx={backButton} onClick={handleClickBack}>
              {backIcon}
            </Fab>
            <Box sx={buttonContainer}>
              {promotion?.hasLocation && (
                <Button
                  variant="contained"
                  sx={button}
                  onClick={navigateToLocation}
                >
                  {t("View Location")}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={toolbarContainer}>
            <Box sx={actionContainer}>
              <LanguageButton variant="contained" sx={actionButton} />
              {/* //TODO:: Add a wheel chair's mode handler function later */}
              {/*  <Button
                  variant="contained"
                  sx={actionButton}
                  onClick={() => {}}
                  startIcon={<RestroomWheelchairIcon />}
                /> */}
            </Box>
            <Box sx={searchBarContainer}>
              <SearchToggleButton
                icon={SearchIcon}
                placeholder={t("Search shops and more...")}
                LinkComponent={Link}
                to="/search"
              />
            </Box>
          </Box>
          <Box sx={mainMenuContainer}>
            <AppNavigation />
            <FooterAction sx={footerActionContainer} />
          </Box>
        </Box>
      </Layout>
      <ImageCarouselModal
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={allImagesUrls}
        onClose={handleClose}
      />
    </>
  )
}
